import ProductImage, { type ProductImageWidths } from "~/domains/product/components/ProductImage";
import classNames from "classnames";
import ClampedText from "~/common/components/ClampedText";
import Parameter from "~/common/components/Parameter";
import { type HousingProduct, HousingProductType } from "~/domains/product/types/ProductData";
import { Link, LinkType } from "~/common/components/Link";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { Translation } from "~/app/i18n/Translation";
import { useFormContext, useWatch } from "react-hook-form";
import type { AddToCartFormDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useTranslation } from "react-i18next";
import { styled } from "~/common/mui";

type HeadlineLevels = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";

interface Props {
  housingProducts: HousingProduct[];
  headlineLevel?: HeadlineLevels;
  className?: string;
}

export const IncludedPartsSection = ({ housingProducts, headlineLevel = "p", className }: Props) => {
  const Headline = headlineLevel;
  const { t } = useTranslation();

  const kitQuantity = useWatch({
    name: "quantity",
    control: useFormContext<AddToCartFormDraft>().control,
    defaultValue: 1,
  });

  return (
    <IncludedPartsRoot className={classNames("scha__included-parts-section", className)}>
      <IncludedPartsTitle as={Headline} className="scha__included-parts-section__title">
        {t("page.productDetails.housing.included.title")}
      </IncludedPartsTitle>
      <IncludedPartsItems className="scha__included-parts-section__items">
        {housingProducts.map((housingProduct) => (
          <IncludedPartsItem key={housingProduct.id} housingProduct={housingProduct} kitQuantity={kitQuantity} />
        ))}
      </IncludedPartsItems>
    </IncludedPartsRoot>
  );
};

interface ItemProps {
  kitQuantity: number;
  housingProduct: HousingProduct;
  className?: string;
}

const IMAGE_WIDTHS: ProductImageWidths = {
  product: "120px",
  manufacturer: "32px",
  brand: "27px",
  gap: "10px",
};

const IncludedPartsItem = ({ housingProduct, className, kitQuantity }: ItemProps) => {
  const { t } = useTranslation();
  const product = housingProduct.product;
  const isHousing = housingProduct.type === HousingProductType.HOUSING;

  let designation = (
    <IncludedPartDesignation
      size={"title-medium"}
      className={"scha__included-parts-item__designation"}
      designation={product.designation}
      isPreferred={product.preferred}
      noCssColor={true}
    />
  );

  if (!isHousing && product.url) {
    designation = (
      <Link variant={LinkType.NAVIGATION} href={product.url} target={"_blank"}>
        {designation}
      </Link>
    );
  }

  const quantity = housingProduct.quantity * kitQuantity;

  return (
    <IncludedPartItemRoot
      className={classNames("scha__included-parts-item", className, isHousing && "scha__included-parts-item--housing")}
    >
      <IncludedPartImage className={"scha__included-parts-item__image"} product={product} widths={IMAGE_WIDTHS} />

      {designation}

      <IncludedPartShortDescription className="scha__included-parts-item__short-description">
        <Translation {...product.shortDescription} />
      </IncludedPartShortDescription>
      <IncludedPartDescription className="scha__included-parts-item__description" text={product.description} lines={2} />
      <Parameter label={t("input.quantity")}>{quantity}</Parameter>
    </IncludedPartItemRoot>
  );
};

const IncludedPartsRoot = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(3.75),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const IncludedPartsTitle = styled("p")(({ theme }) => ({
  ...theme.typography.titleLarge,
  marginBottom: theme.spacing(3.75),
}));

const IncludedPartsItems = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 25%)",
  rowGap: theme.spacing(3.75),
  [theme.breakpoints.down("tablet")]: {
    gridTemplateColumns: "repeat(1, 100%)",
    rowGap: 0,
  },
}));

const IncludedPartItemRoot = styled("div")(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  "&:not(:nth-child(4n))": {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  [theme.breakpoints.down("tablet")]: {
    padding: `${theme.spacing(2)} 0`,
    borderRight: "unset !important",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "unset",
      padding: `${theme.spacing(2)} 0 0 0`,
    },
  },
  "&.scha__included-parts-item--housing .scha__included-parts-item__designation": {
    fontWeight: "normal",
  },
  "&.scha__included-parts-item--housing .scha__included-parts-item__short-description": {
    visibility: "hidden",
  },
}));

const IncludedPartImage = styled(ProductImage)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
}));

const IncludedPartDesignation = styled(ProductDesignation)(({ theme }) => ({
  marginBottom: theme.spacing(1.25),
  ...theme.typography.titleMedium,
  fontWeight: theme.typography.fontWeightBold,
}));

const IncludedPartShortDescription = styled("p")(({ theme }) => ({
  marginBottom: theme.spacing(1.25),
  ...theme.typography.titleMedium,
}));

const IncludedPartDescription = styled(ClampedText)(({ theme }) => ({
  marginBottom: theme.spacing(1.25),
  ...theme.typography.bodyMedium,
}));
